/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { Fragment, useEffect, useState } from 'react';

import { jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';
import { IntlProvider } from 'react-intl-next';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import CollapseHorizontalIcon from '@atlaskit/icon/core/collapse-horizontal';
import ExpandHorizontalIcon from '@atlaskit/icon/core/expand-horizontal';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import Link from '@atlaskit/link';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Inline, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { type SideBySideExperiment } from '@atlassian/search-experiment';
import { SearchPageSideBySide } from '@atlassian/search-page';

import {
	FeedbackFormSectionLayout,
	type FeedbackFormSubmissionParams,
	GleanSearchResults,
	IframeSearchPage,
	MoneyIcon,
	SearchBar,
	SearchFeedbackForm,
	SubmissionConfetti,
} from '../../common/ui';
import {
	ANALYTIC_TYPE,
	ANALYTICS_CHANNEL,
	EVENT_TYPE,
	MODAL_ACTION,
	SideBySideAnalyticsProvider,
} from '../../utils/analytics';
import { getExperimentsFromSearchParam } from '../../utils/experiments';
import {
	FeedbackRating,
	formatPresetAndFreeformFeedback,
	getEnumValueFromForm,
	getFilters,
} from '../../utils/feedback';
import { captureScreenShot, type ScreenshotReturnType } from '../../utils/screenshot';

import { type ComparisonFeedbackPayload, type EnterpriseFeedbackComparisonProps } from './types';

const searchItemStyles = xcss({
	flexBasis: '50%',
	flexGrow: 1,
	height: '1000px',
	maxWidth: '799px',
	overflowX: 'hidden',
});

const iframeSearchResultsStyle = xcss({
	marginTop: 'space.300',
});

const rovoSideBySideResultsStyle = xcss({
	marginTop: 'space.negative.100',
	overflowY: 'auto',
});

const displayNoneStyle = xcss({
	display: 'none',
});
const visibilityHiddenStyle = xcss({
	visibility: 'hidden',
});
const searchItemFullWidthStyles = xcss({
	flexBasis: 0,
	flexGrow: 1,
	height: '1000px',
	maxWidth: '100%',
	overflowX: 'hidden',
});
const verticalDividerStyles = xcss({
	background: `${token('color.border')}`,
	minWidth: '2px',
	marginLeft: 'space.500',
	marginRight: 'space.500',
});

const longVerticalDividerStyles = xcss({
	height: '1000px',
});

const inlineStyles = xcss({
	width: '80vw',
	marginLeft: 'auto',
	marginRight: 'auto',
});

const FEEDBACK_COMPARISON_URL = '/gateway/api/xpsearch-aggregator/feedback/v1/comparison';
const SUCCESS_FLAG = 'feedback-submitted-successfully';

const topSectionStyles = xcss({
	marginTop: 'space.150',
	marginBottom: 'space.150',
	marginLeft: 'auto',
	marginRight: 'auto',
	width: '80vw',
});

const linkStyles = xcss({
	justifyContent: 'center',
	background: 'none',
	alignItems: 'center',
	marginBottom: 'space.075',
});

const iconStyles = xcss({
	paddingRight: 'space.075',
});
const expandCollapseButtonsStyles = xcss({
	width: '80vw',
	marginLeft: 'auto',
	marginRight: 'auto',
	display: 'flex',
	justifyContent: 'space-between',
});

export const isBase64 = (imageData: string): boolean => {
	if (!imageData || !imageData.length) {
		return false;
	}

	const base64Regex = /^(?:[A-Za-z0-9+\/]{4})*?(?:[A-Za-z0-9+\/]{2}==|[A-Za-z0-9+\/]{3}=)?$/;
	return base64Regex.test(imageData);
};

export const RedesignEnterpriseFeedbackComparisonPage = ({
	testId,
	shell,
	callbacks,
	mapUserIdToProfileUrl,
	isRovoVsRovo = true,
}: EnterpriseFeedbackComparisonProps): JSX.Element => {
	const currentQueryParams = new URLSearchParams(window.location.href);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [searchedAt, setSearchedAt] = useState<string>();
	const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState<boolean>(false);
	const [flags, setFlags] = useState<Array<string>>([]);
	const [isRated, setIsRated] = useState<boolean>(false);
	const [searchSessionId, setSearchSessionId] = useState<string>('');
	const { updateQueryParameters } = callbacks;
	const { cloudId, activationId, user, queryParams } = shell;
	const [textQueryParam, setTextQueryParam] = useState<string>(queryParams?.text);
	const [allQueryParams, setAllQueryParams] = useState<any>(queryParams);
	const [leftExperiments, setLeftExperiments] = useState<Array<SideBySideExperiment>>([]);
	const [rightExperiments, setRightExperiments] = useState<Array<SideBySideExperiment>>([]);
	const [top5ResultsEqual, setTop5ResultsEqual] = useState<boolean>(false);
	const [leftTop5Links, setLeftTop5Links] = useState<string[]>([]);
	const [rightTop5Links, setRightTop5Links] = useState<string[]>([]);
	const [rightQueryText, setRightSideQueryText] = useState<string>();
	const [leftSideExpanded, setLeftSideExpanded] = useState<boolean>(false);
	const [rightSideExpanded, setRightSideExpanded] = useState<boolean>(false);

	const workspaceARI = `ari:cloud:confluence:${cloudId}:workspace/${activationId}`;
	const userId = user?.id;

	const isIFramePage = currentQueryParams.get('isIFramePage');
	const querySearchSessionId = currentQueryParams.get('searchSessionId');

	const analyticsEvent = createAnalyticsEvent({
		name: 'viewedComparisonFeedbackScreen',
		type: ANALYTIC_TYPE.SEND_SCREEN_EVENT,
		eventType: EVENT_TYPE.SCREEN,
		source: ANALYTICS_CHANNEL,
		attributes: {
			isSideAOnLeft: true,
		},
	});

	useEffect(() => {
		const handleMessage = (event: any) => {
			const { type, queryParams, top5LinksFromIframe, queryTextFromIframe } = event.data;
			if (type === 'UPDATE_FILTER') {
				setAllQueryParams(queryParams);
			}
			if (type === 'TOP_5_LINKS') {
				setRightSideQueryText(queryTextFromIframe);
				setRightTop5Links(top5LinksFromIframe);
			}
		};
		const unbind = bind(window, {
			type: 'message',
			listener: handleMessage,
		});
		return () => {
			unbind();
		};
	}, []);

	useEffect(() => {
		if (textQueryParam) {
			setSearchedAt(new Date().toISOString());
			// syncing search session id across the iframe
			if (querySearchSessionId && isIFramePage) {
				setSearchSessionId(querySearchSessionId);
			} else {
				// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
				setSearchSessionId(uuid());
			}
		}
		setIsFeedbackSubmitted(false);
	}, [textQueryParam, querySearchSessionId, isIFramePage]);

	useEffect(() => {
		setTextQueryParam(queryParams?.text);
		setAllQueryParams(queryParams);
		if (queryParams?.leftExperiments) {
			setLeftExperiments(getExperimentsFromSearchParam(queryParams.leftExperiments));
		}
		if (queryParams?.rightExperiments) {
			setRightExperiments(getExperimentsFromSearchParam(queryParams.rightExperiments));
		}
		if (isIFramePage) {
			window.parent.postMessage({ type: 'UPDATE_FILTER', queryParams });
		}
	}, [queryParams, isIFramePage]);

	useEffect(() => {
		analyticsEvent.fire(ANALYTICS_CHANNEL);
	}, [analyticsEvent]);

	useEffect(() => {
		if (!isRovoVsRovo) {
			return;
		}

		const handleMutation = () => {
			const setLinks = !!isIFramePage ? setRightTop5Links : setLeftTop5Links;

			const selector = '[data-testid="search-page-result-link"]';
			// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
			const results = Array.from(document.querySelectorAll(selector));

			if (results.length === 0) {
				setLinks([]);
			} else {
				const links = results
					.slice(0, 5)
					.map((link) => link.getAttribute('href'))
					.filter(Boolean) as string[];

				setLinks(links);
			}
		};

		const observer = new MutationObserver(handleMutation);

		// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
		const container = document.querySelector(`[data-testid="${testId}"]`);
		if (!container) {
			return;
		}

		observer.observe(container, {
			childList: true,
			subtree: true,
		});

		return () => observer.disconnect();
	}, [isIFramePage, testId, isRovoVsRovo]);

	useEffect(() => {
		if (!(rightTop5Links.length > 0 && !!isIFramePage)) {
			return;
		}
		window.parent.postMessage(
			{
				type: 'TOP_5_LINKS',
				top5LinksFromIframe: rightTop5Links,
				queryTextFromIframe: queryParams?.text,
			},
			'*',
		);
	}, [rightTop5Links, isIFramePage, queryParams?.text]);

	useEffect(() => {
		if (!(leftTop5Links?.length > 0 && rightTop5Links?.length > 0)) {
			return;
		}
		const areLinksEqual = leftTop5Links.every((link, index) => link === rightTop5Links[index]);
		const queryTextEqual = queryParams?.text === rightQueryText;
		if (queryTextEqual && !areLinksEqual) {
			setTop5ResultsEqual(false);
		}
	}, [leftTop5Links, rightTop5Links, queryParams?.text, rightQueryText]);

	const submitComparisonForm = async ({
		atlassianRating,
		ratingReasons,
		feedback,
		shouldAttachScreenshot,
		action,
	}: FeedbackFormSubmissionParams) => {
		const atlassianRatingEnum = getEnumValueFromForm(atlassianRating);

		if (!cloudId || !userId || !activationId || !atlassianRatingEnum || !queryParams) {
			return;
		}

		let screenshotResult: ScreenshotReturnType | undefined; // Declare screenshotResult outside and above the if block

		if (shouldAttachScreenshot) {
			screenshotResult = await captureScreenShot();

			if (screenshotResult.imageData.includes('Error')) {
				const analyticsEvent = createAnalyticsEvent({
					action: 'errored',
					actionSubject: 'comparisonFormScreenshot',
					type: ANALYTIC_TYPE.SEND_TRACK_EVENT,
					eventType: EVENT_TYPE.TRACK,
					source: ANALYTICS_CHANNEL,
					attributes: {
						error: screenshotResult.imageData,
					},
				});
				analyticsEvent.fire(ANALYTICS_CHANNEL);
			}
		} else {
			screenshotResult = { imageData: 'declined', imageSize: 0, startImageSize: 0 };
		}

		const formattedFeedback = formatPresetAndFreeformFeedback(ratingReasons, feedback);
		const filters = getFilters(queryParams);

		const feedbackPayload: ComparisonFeedbackPayload = {
			workspace: workspaceARI,
			eventTriggeredBy: user?.id,
			searchQuery: textQueryParam,
			atlassianRating: atlassianRatingEnum,
			gleanRating: FeedbackRating.ONE,
			// Conditionally include screenshot if screenshotResult has a value
			...(screenshotResult && { screenshot: screenshotResult.imageData }),
			// include text input if it was provided. otherwise, submit only the scale rating
			...(formattedFeedback && { feedback: formattedFeedback }),
			searchedAt,
			searchSessionRef: searchSessionId,
			...(filters && { ...filters }),
		};

		const analyticsEvent = createAnalyticsEvent({
			action: 'submitted',
			actionSubject: 'comparisonRatingForm',
			type: ANALYTIC_TYPE.SEND_TRACK_EVENT,
			eventType: EVENT_TYPE.TRACK,
			source: ANALYTICS_CHANNEL,
			attributes: {
				rating: atlassianRating,
				isRedesign: true,
				action,
				ratingReasons,
				isSideAOnLeft: true,
				...(screenshotResult && {
					startImageSize: screenshotResult.startImageSize,
					finalImageSize: screenshotResult.imageSize,
				}),
				includesScreenshot: !!screenshotResult && isBase64(screenshotResult.imageData),
			},
		});

		const response = await fetch(FEEDBACK_COMPARISON_URL, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(feedbackPayload),
		});

		if (!response.ok) {
			const analyticsEvent = createAnalyticsEvent({
				action: 'errored',
				actionSubject: 'comparisonRatingForm',
				type: ANALYTIC_TYPE.SEND_TRACK_EVENT,
				eventType: EVENT_TYPE.TRACK,
				source: ANALYTICS_CHANNEL,
				attributes: {
					errorStatus: response.status,
				},
			});
			analyticsEvent.fire(ANALYTICS_CHANNEL);
			return;
		}

		if (action === MODAL_ACTION.SUBMITTED) {
			setFlags((state) => [SUCCESS_FLAG, ...state]);
		}
		analyticsEvent.fire(ANALYTICS_CHANNEL);
		setIsFeedbackSubmitted(true);
	};

	const updateSearchParams = ({ text }: any) => {
		if (updateQueryParameters) {
			// we want to pass all query params to the callback to ensure applied filters persist with new searches,
			// overriding only the text param for new searches
			// (matching the behavior of the regular search page)
			const filteredQueryParams = { ...allQueryParams, text };
			updateQueryParameters(filteredQueryParams);
			setIsFeedbackSubmitted(false);

			if (isRovoVsRovo) {
				// Reset sides comparison
				setTop5ResultsEqual(false);
				setLeftTop5Links([]);
				setRightTop5Links([]);
				setLeftSideExpanded(false);
				setRightSideExpanded(false);
			}
		}
	};

	const handleFlagDismissed = () => {
		setFlags(flags.slice(1));
	};

	const handleRatingClick = () => {
		setIsRated(true);
		setFlags((state) => [...state, 'rating-flag']);
	};

	// Match shell with the vanilla SearchPage object
	const shellWithAnalyticsObject = {
		...shell,
		analyticsAttributes: { activationId: shell.activationId },
		searchSessionId,
	};

	const sideAResults = (
		<SearchPageSideBySide
			shell={shellWithAnalyticsObject}
			callbacks={callbacks}
			mapUserIdToProfileUrl={mapUserIdToProfileUrl}
			experiments={leftExperiments}
		/>
	);
	const sideBResults = isRovoVsRovo ? (
		<IframeSearchPage
			queryString={textQueryParam}
			searchSessionId={searchSessionId}
			queryParams={allQueryParams}
		/>
	) : (
		<GleanSearchResults query={textQueryParam} />
	);

	const onExpandCollapseClick = (expandRightSide = false) => {
		const setExpanded = expandRightSide ? setRightSideExpanded : setLeftSideExpanded;
		const currentState = expandRightSide ? rightSideExpanded : leftSideExpanded;

		setExpanded(!currentState);
	};
	const expandCollapseButtonsProps = [
		{
			hidden: rightSideExpanded,
			expanded: leftSideExpanded,
			// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
			label: 'left side',
			onClick: () => onExpandCollapseClick(),
		},
		{
			hidden: leftSideExpanded,
			expanded: rightSideExpanded,
			// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
			label: 'right side',
			onClick: () => onExpandCollapseClick(true),
		},
	];

	return (
		<Box testId={testId}>
			{!!isIFramePage ? (
				<SearchPageSideBySide
					shell={shellWithAnalyticsObject}
					callbacks={callbacks}
					mapUserIdToProfileUrl={mapUserIdToProfileUrl}
					experiments={rightExperiments}
				/>
			) : (
				<React.Fragment>
					{isFeedbackSubmitted && <SubmissionConfetti />}
					<Stack>
						<Flex direction="column" xcss={topSectionStyles}>
							<Flex xcss={linkStyles}>
								<Box xcss={iconStyles}>
									<MoneyIcon />
								</Box>
								<Link
									href="https://hello.atlassian.net/wiki/spaces/SDE1/blog/2024/10/22/4354842094/Rovo+Search+October+Gold+Rush+Make+more+money+while+you+work"
									rel="noopener noreferrer"
									target="_blank"
									// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
								>
									Rovo Search October Gold Rush: Make more money while you work!
								</Link>
							</Flex>

							<FeedbackFormSectionLayout
								showForm={!isFeedbackSubmitted && !top5ResultsEqual}
								components={{
									form: (
										<SearchFeedbackForm
											isSideAOnLeft={true}
											onSubmit={submitComparisonForm}
											onRatingClick={handleRatingClick}
										/>
									),
								}}
							/>
							<SearchBar
								textQueryParam={textQueryParam}
								updateQueryParameters={updateSearchParams}
							/>
						</Flex>
						{textQueryParam && (
							<Box>
								{!top5ResultsEqual && (
									<Box xcss={expandCollapseButtonsStyles}>
										{!top5ResultsEqual &&
											expandCollapseButtonsProps.map(({ hidden, expanded, label, onClick }) => (
												<Box xcss={hidden && visibilityHiddenStyle}>
													<IconButton
														key={label}
														appearance="subtle"
														icon={expanded ? CollapseHorizontalIcon : ExpandHorizontalIcon}
														label={`${expanded ? 'Collapse' : 'Expand'} ${label}`}
														isTooltipDisabled={false}
														onClick={onClick}
														testId={`expand-collapse-${label.replace(' ', '-')}-btn`}
													/>
												</Box>
											))}
									</Box>
								)}
								<Inline grow="fill" alignInline="center" xcss={inlineStyles}>
									<Fragment>
										<Box
											xcss={[
												searchItemStyles,
												rovoSideBySideResultsStyle,
												(top5ResultsEqual || leftSideExpanded) && searchItemFullWidthStyles,
												rightSideExpanded && displayNoneStyle,
											]}
										>
											{sideAResults}
										</Box>
										<Box
											xcss={[
												verticalDividerStyles,
												longVerticalDividerStyles,
												top5ResultsEqual && displayNoneStyle,
												(leftSideExpanded || rightSideExpanded) && displayNoneStyle,
											]}
										/>
										<Box
											xcss={[
												searchItemStyles,
												iframeSearchResultsStyle,
												(top5ResultsEqual || leftSideExpanded) && displayNoneStyle,
												rightSideExpanded && searchItemFullWidthStyles,
											]}
										>
											{sideBResults}
										</Box>
									</Fragment>
								</Inline>
							</Box>
						)}
						{(isFeedbackSubmitted || isRated) && (
							// display success flag when comparison feedback is submitted successfully
							// (auto dismisses after 8 seconds)
							<FlagGroup
								onDismissed={() => {
									handleFlagDismissed();
								}}
							>
								{flags.map((flagId) => {
									return (
										<AutoDismissFlag
											appearance="success"
											icon={<SuccessIcon label="Success" color="currentColor" spacing="spacious" />}
											id={flagId}
											key={flagId}
											title={
												flagId === SUCCESS_FLAG
													? 'Thanks for making search better.'
													: 'Thanks! We got your rating.'
											}
										/>
									);
								})}
							</FlagGroup>
						)}
					</Stack>
				</React.Fragment>
			)}
		</Box>
	);
};

export function EnterpriseFeedbackComparisonPage(props: EnterpriseFeedbackComparisonProps): JSX.Element {
	const { shell } = props;
	return (
		<SideBySideAnalyticsProvider
			userId={shell.user?.id}
			tenantId={shell.cloudId}
			entryPoint={props.entryPoint}
		>
			<IntlProvider locale="en">
				<RedesignEnterpriseFeedbackComparisonPage {...props} />
			</IntlProvider>
		</SideBySideAnalyticsProvider>
	);
}
